/**
 * Finance & Business (and its sub-tags): a scrolling market strip under the tag's header, on every screen size.
 * Market open or closed, the naira against the dollar, pound, euro and Canadian dollar (with the move since
 * the day before), Brent crude and gold, then the NGX's ten largest companies by market value with today's move.
 *
 * Data: GET /api/voucha-markets/data (the markets cache; scripts/markets/refresh_market_data.php keeps it).
 * The strip pauses under the pointer or a finger, and doesn't move for people who ask for reduced motion
 * (it scrolls sideways by hand instead). Hidden until the data is in.
 */
import app from 'flarum/forum/app';
import IndexPage from 'flarum/forum/components/IndexPage';
import { override } from 'flarum/common/extend';
import Link from 'flarum/common/components/Link';
const CURRENCIES = ['USD', 'GBP', 'EUR', 'CAD'];
const SYMBOL = {
  USD: '$',
  GBP: '£',
  EUR: '€',
  CAD: 'C$'
};
let markets = null;
let loading = false;
function load() {
  if (markets || loading) return;
  loading = true;
  app.request({
    method: 'GET',
    url: app.forum.attribute('apiUrl') + '/voucha-markets/data',
    errorHandler: () => {}
  }).then(r => {
    markets = r && r.markets || {};
    m.redraw();
  }).catch(() => {
    markets = {};
  });
}
export function isFinanceTag(slug) {
  if (!slug || slug.indexOf(',') !== -1) return false;
  if (slug === 'finance-business') return true;
  const tag = (app.store.all('tags') || []).find(t => t.slug() === slug);
  const parent = tag && tag.parent && tag.parent();
  return !!(parent && parent.slug && parent.slug() === 'finance-business');
}
const naira = v => '₦' + Number(v).toLocaleString('en-US', {
  maximumFractionDigits: 2
});
function move(pct) {
  const c = Number(pct);
  if (!isFinite(c) || Math.abs(c) < 0.005) return m('span.VouchaTicker-move', '0.00%');
  return m('span.VouchaTicker-move' + (c > 0 ? '.is-up' : '.is-down'), (c > 0 ? '▲ ' : '▼ ') + Math.abs(c).toFixed(2) + '%');
}
function items(copy) {
  const tab = copy ? {
    tabindex: -1
  } : {};
  const list = [];
  const status = markets.market_status && markets.market_status.data && markets.market_status.data.status;
  if (status) {
    const open = String(status).toLowerCase() === 'open';
    list.push(m('span.VouchaTicker-item.VouchaTicker-status' + (open ? '.is-open' : ''), [m('span.VouchaTicker-dot'), 'NGX ' + (open ? 'open' : 'closed')]));
  }
  const fx = markets.exchange_rates && markets.exchange_rates.data || {};
  const rates = fx.rates || {};
  const prev = fx.prev || {};
  CURRENCIES.forEach(code => {
    const r = Number(rates[code]);
    if (!(r > 0)) return;
    const p = Number(prev[code]);
    list.push(m(Link, {
      ...tab,
      className: 'VouchaTicker-item',
      href: '/markets',
      title: SYMBOL[code] + '1 = ' + naira(r)
    }, [m('b', code + '/NGN'), m('span', naira(r)), p > 0 ? move((r - p) / p * 100) : null]));
  });

  // Brent crude and gold (world prices, Markets page "Commodity prices")
  const commodities = markets.commodities && markets.commodities.data && markets.commodities.data.items || {};
  ['brent', 'gold'].forEach(k => {
    const c = commodities[k];
    if (!c || !(Number(c.price) > 0)) return;
    const p = Number(c.prev);
    list.push(m(Link, {
      ...tab,
      className: 'VouchaTicker-item',
      href: '/commodities#' + k,
      title: c.name + ', US dollars per ' + (k === 'gold' ? 'ounce' : 'barrel')
    }, [m('b', k === 'brent' ? 'BRENT' : 'GOLD'), m('span', '$' + Number(c.price).toLocaleString('en-US', {
      maximumFractionDigits: 2
    })), p > 0 ? move((Number(c.price) - p) / p * 100) : null]));
  });
  const stocks = (markets.stocks && markets.stocks.data && markets.stocks.data.stocks || []).filter(s => s && s.symbol && Number(s.current_price) > 0).sort((a, b) => (b.market_cap || 0) - (a.market_cap || 0)).slice(0, 10);
  stocks.forEach(s => {
    list.push(m(Link, {
      ...tab,
      className: 'VouchaTicker-item',
      href: '/markets/' + String(s.symbol).toLowerCase(),
      title: s.name || s.symbol
    }, [m('b', s.symbol), m('span', naira(s.current_price)), move(s.change_percent)]));
  });
  return list;
}
class Ticker {
  oninit() {
    load();
  }
  view() {
    if (!markets) return null;
    const list = items();
    if (list.length < 3) return null;
    // two copies side by side; the strip slides one copy's width and starts over, so it never runs out
    const seconds = Math.max(30, list.length * 5);
    return m('div.VouchaTicker', {
      role: 'region',
      'aria-label': 'Market prices'
    }, m('div.VouchaTicker-track', {
      style: {
        animationDuration: seconds + 's'
      }
    }, [m('div.VouchaTicker-set', list), m('div.VouchaTicker-set', {
      'aria-hidden': 'true'
    }, items(true))]));
  }
}
app.initializers.add('voucha-theme-ticker', function () {
  override(IndexPage.prototype, 'hero', function (original) {
    const hero = original();
    let slug = '';
    try {
      slug = m.route.param('tags') || '';
    } catch (e) {}
    return isFinanceTag(slug) ? [hero, m(Ticker)] : hero;
  });
});